import React, { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import useSWR from 'swr'
import { fetchFeedCatalog, uploadImage, deleteImage, type FeedSummary } from '../services/api'
import { useDebounce } from '../hooks'
import { Badge, Button, ImageSlot } from '../components'
import pageStyles from '../styles/page.module.scss'
import listTableStyles from '../styles/list-table.module.scss'
import buttonStyles from '../components/Button.module.scss'
import styles from './FeedsList.module.scss'

const feedTitle = (feed: FeedSummary): string => feed.display_name || feed.name

const FeedsList: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('')

  const debouncedSearchTerm = useDebounce(searchTerm, 300)

  const { data, isLoading, error, mutate } = useSWR(
    ['feeds-catalog'],
    () => fetchFeedCatalog(),
    { revalidateOnFocus: false }
  )

  const filteredFeeds = useMemo(() => {
    const term = debouncedSearchTerm.trim().toLowerCase()
    if (!term) return data?.feeds || []
    return (data?.feeds || []).filter((feed) =>
      `${feed.display_name || ''} ${feed.name}`.toLowerCase().includes(term)
    )
  }, [data, debouncedSearchTerm])

  const handleUploadCard = async (feed: FeedSummary, file: File) => {
    await uploadImage('feed', feed.feed_id, file, { image_type: 'channel_card' })
    await mutate()
  }

  const handleRemoveCard = async (feed: FeedSummary) => {
    if (!feed.channel_card_image_id) return
    if (!window.confirm(`Remove the channel card for ${feed.display_name || feed.name}?`)) return
    await deleteImage('feed', feed.feed_id, feed.channel_card_image_id)
    await mutate()
  }

  if (error) {
    return (
      <div className={pageStyles.pageContainer}>
        <div className={pageStyles.error}>
          <h2>Error loading feeds</h2>
          <p>Failed to fetch feeds: {(error as Error).message}</p>
          <Button variant="danger" onClick={() => mutate()}>Retry</Button>
        </div>
      </div>
    )
  }

  return (
    <div className={pageStyles.pageContainer}>
      <div className={pageStyles.pageHeader}>
        <h1>Feeds &amp; Channels</h1>
        <p>Assign the 16:9 card shown when a feed appears on an app rail.</p>
      </div>

      <div className={listTableStyles.searchSection}>
        <div className={listTableStyles.searchForm}>
          <div className={listTableStyles.searchInputGroup}>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search feeds by name..."
              className={listTableStyles.searchInput}
            />
            {searchTerm && (
              <button type="button" onClick={() => setSearchTerm('')} className={listTableStyles.clearButton}>
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {isLoading && !data ? (
        <div className={pageStyles.loading}>
          <h3>Loading feeds...</h3>
          <p>Please wait while we fetch the feed catalog.</p>
        </div>
      ) : filteredFeeds.length === 0 ? (
        <div className={listTableStyles.noResults}>
          <h3>No feeds found</h3>
          <p>
            {debouncedSearchTerm
              ? `No feeds match "${debouncedSearchTerm}".`
              : 'Unable to load feed data.'}
          </p>
        </div>
      ) : (
        <>
          <div className={`${listTableStyles.tableHeader} ${styles.listHeader}`}>
            <div>Image</div>
            <div>Name</div>
            <div>Feed key</div>
            <div>Type</div>
            <div>Status</div>
            <div></div>
          </div>

          <div className={listTableStyles.tableBody}>
            {filteredFeeds.map((feed) => (
              <div key={feed.feed_id} className={`${listTableStyles.tableRow} ${styles.listItem}`}>
                <div className={styles.imageCell}>
                  <ImageSlot
                    image={feed.channel_card_url ? { url: feed.channel_card_url } : null}
                    aspectRatio="16 / 9"
                    compact
                    onUpload={(file) => handleUploadCard(feed, file)}
                    onRemove={feed.has_channel_card ? () => handleRemoveCard(feed) : undefined}
                  />
                </div>

                <div className={listTableStyles.cellPrimary}>
                  <p className={listTableStyles.primaryText}>{feedTitle(feed)}</p>
                </div>

                <div className={listTableStyles.cellId}>
                  {feed.name}
                </div>

                <div className={listTableStyles.cellBadges}>
                  <Badge variant={feed.is_linear ? 'primary' : 'secondary'}>
                    {feed.is_linear ? 'Linear' : 'Court'}
                  </Badge>
                  {feed.is_gated && <Badge variant="warning">Gated</Badge>}
                </div>

                <div className={listTableStyles.cellBadges}>
                  <Badge variant={feed.published ? 'success' : 'neutral'}>
                    {feed.published ? 'Published' : 'Draft'}
                  </Badge>
                </div>

                <div className={`${listTableStyles.cellActions} ${styles.actionsCell}`}>
                  <Link
                    to={`/feeds/${feed.feed_id}`}
                    className={`${buttonStyles.button} ${buttonStyles.primary} ${buttonStyles.medium}`}
                  >
                    View
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

export default FeedsList
